/**
 * DataGrid Context - Global density settings with local override support
 * Density options: compact, normal, comfortable
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Density configurations
export const DENSITY_CONFIG = {
  compact: {
    label: 'Compacto',
    rowHeight: 36,
    headerHeight: 36,
    fontSize: '0.75rem',
    cellPadding: '0.5rem',
  },
  normal: {
    label: 'Normal',
    rowHeight: 48,
    headerHeight: 44,
    fontSize: '0.8125rem',
    cellPadding: '0.75rem',
  },
  comfortable: {
    label: 'Confortável',
    rowHeight: 60,
    headerHeight: 52,
    fontSize: '0.875rem',
    cellPadding: '1rem',
  },
};

const STORAGE_KEY = 'datagrid-density';

const DataGridContext = createContext({
  density: 'normal',
  setDensity: () => {},
  densityConfig: DENSITY_CONFIG.normal,
});

export function DataGridProvider({ children }) {
  const [density, setDensityState] = useState(() => {
    // Load from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && DENSITY_CONFIG[saved]) {
        return saved;
      }
    }
    return 'normal';
  });

  // Save to localStorage when density changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, density);
  }, [density]);

  const setDensity = useCallback((newDensity) => {
    if (DENSITY_CONFIG[newDensity]) {
      setDensityState(newDensity);
    }
  }, []);

  const densityConfig = DENSITY_CONFIG[density];

  return (
    <DataGridContext.Provider value={{ density, setDensity, densityConfig }}>
      {children}
    </DataGridContext.Provider>
  );
}

export function useDataGridDensity() {
  return useContext(DataGridContext);
}

/**
 * Hook for local density override
 * Returns the effective density (local override or global)
 */
export function useLocalDensity(localDensity = null) {
  const { density: globalDensity, densityConfig: globalConfig } = useDataGridDensity();
  
  const effectiveDensity = localDensity || globalDensity;
  const effectiveConfig = DENSITY_CONFIG[effectiveDensity] || globalConfig;
  
  return {
    density: effectiveDensity,
    config: effectiveConfig,
  };
}

export default DataGridContext;
